import axios from "axios";
import {
  AirportFlights,
  AirportKpiMetric,
  AirportNotifications,
  AirportOperationalData,
  AirportOperationalStatus,
  AirportOtp,
  MultiAirportDateRangePayloads,
  MultiAirportFlightsRequest,
  MultiAirportKpiResponse,
  MultiAirportNotificationCountRequest,
  MultiAirportNotificationCountResponse,
  MultiAirportOperationalDataFailure,
  MultiAirportOperationalDataResult,
  MultiAirportOperationalDataTarget,
  MultiAirportOtpRequest,
} from "../../models/airports/AirportConfig";

export const MULTI_AIRPORT_OTP_URL =
  process.env.REACT_APP_API_MULTI_AIRPORT_OTP ?? "";
export const MULTI_AIRPORT_FLIGHTS_URL =
  process.env.REACT_APP_API_FLIGHTSUMMARY?.replace("{airportCode}", "ANY") ??
  "";
export const MULTI_AIRPORT_NOTIFICATIONS_URL =
  process.env.REACT_APP_API_MULTI_AIRPORT_NOTIFICATION_COUNT ?? "";

export const EMPTY_AIRPORT_OPERATIONAL_DATA: AirportOperationalData = {
  otp: {
    overall: { value: 0, status: "poor" },
    arrival: { value: 0, status: "poor" },
    departure: { value: 0, status: "poor" },
  },
  flights: {
    total: { value: 0 },
    delayed: { value: 0 },
    cancelled: { value: 0 },
  },
  averageDelay: { delay: { value: 0, status: "poor" } },
  notifications: { critical: 0, high: 0 },
};

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const normalizeAirportCode = (airportCode: string): string =>
  airportCode.trim().toUpperCase();

const isOperationalStatus = (
  status: string | undefined
): status is AirportOperationalStatus =>
  status === "good" || status === "moderate" || status === "poor";

const findMetric = (
  response: MultiAirportKpiResponse | undefined,
  type: string
): AirportKpiMetric => {
  const item = response?.result?.find(
    (resultItem) => resultItem.type.toLowerCase() === type.toLowerCase()
  );
  const normalizedStatus = item?.status?.toLowerCase();

  return {
    value: Number(item?.kpiValue) || 0,
    status: isOperationalStatus(normalizedStatus) ? normalizedStatus : "poor",
  };
};

const findValue = (
  response: MultiAirportKpiResponse | undefined,
  type: string
): number => {
  const item = response?.result?.find(
    (resultItem) => resultItem.type.toLowerCase() === type.toLowerCase()
  );
  return Number(item?.kpiValue) || 0;
};

const findBreakdownValue = (
  response: MultiAirportKpiResponse | undefined,
  parentType: string,
  type: string
): number => {
  const parent = response?.result?.find(
    (resultItem) => resultItem.type.toLowerCase() === parentType.toLowerCase()
  );
  const item = parent?.breakdown?.find(
    (breakdownItem) => breakdownItem.type.toLowerCase() === type.toLowerCase()
  );

  return Number(item?.kpiValue) || 0;
};

export const adaptMultiAirportOtp = (
  response: MultiAirportKpiResponse | undefined
): AirportOtp => {
  if (!response || response.error) {
    return clone(EMPTY_AIRPORT_OPERATIONAL_DATA.otp);
  }

  return {
    overall: findMetric(response, "Overall"),
    arrival: findMetric(response, "Arrivals"),
    departure: findMetric(response, "Departures"),
  };
};

export const adaptMultiAirportFlights = (
  response: MultiAirportKpiResponse | undefined
): AirportFlights => {
  if (!response || response.error) {
    return clone(EMPTY_AIRPORT_OPERATIONAL_DATA.flights);
  }

  return {
    total: { value: findValue(response, "Overall") },
    delayed: {
      value: findBreakdownValue(response, "Overall", "Delayed"),
    },
    cancelled: {
      value: findBreakdownValue(response, "Overall", "Cancelled"),
    },
  };
};

export const adaptMultiAirportNotifications = (
  response: MultiAirportNotificationCountResponse | undefined
): AirportNotifications => {
  if (!response || response.error) {
    return clone(EMPTY_AIRPORT_OPERATIONAL_DATA.notifications);
  }

  return {
    critical: Number(response.critical) || 0,
    high: Number(response.high) || 0,
  };
};

const getErrorMessage = (reason: unknown): string => {
  if (reason instanceof Error) {
    return reason.message;
  }
  if (typeof reason === "string") {
    return reason;
  }
  if (
    reason &&
    typeof reason === "object" &&
    "message" in reason &&
    typeof reason.message === "string"
  ) {
    return reason.message;
  }
  return "Unable to load multi-airport operational data";
};

const createFailure = (
  airportCode: string,
  target: MultiAirportOperationalDataTarget,
  stage: MultiAirportOperationalDataFailure["stage"],
  reason: unknown
): MultiAirportOperationalDataFailure => ({
  airportCode,
  target,
  stage,
  message: getErrorMessage(reason),
});

const getHeaders = (accessToken: string) => ({
  "x-api-key": process.env.REACT_APP_APIM_KEY,
  Authorization: `Bearer ${accessToken}`,
  "Content-Type": "application/json;charset=UTF-8",
});

const postJson = <T>(
  url: string,
  payload: unknown,
  accessToken: string,
  signal?: AbortSignal
): Promise<T> =>
  axios
    .post<T>(url, JSON.stringify(payload), {
      headers: getHeaders(accessToken),
      signal,
    })
    .then(({ data }) => data);

const getResponseByAirport = <
  T extends { airportCode: string; error?: unknown },
>(
  responses: T[]
): Map<string, T> =>
  new Map(
    responses.map((response) => [
      normalizeAirportCode(response.airportCode),
      response,
    ])
  );

const getUniqueAirportCodes = (airportCodes: string[]): string[] => {
  const result: string[] = [];
  const seen = new Set<string>();

  airportCodes.forEach((airportCode) => {
    const normalizedCode = normalizeAirportCode(airportCode);
    if (normalizedCode && !seen.has(normalizedCode)) {
      seen.add(normalizedCode);
      result.push(normalizedCode);
    }
  });

  return result;
};

const getSettledResponses = <T>(
  result: PromiseSettledResult<T[]>,
  airportCodes: string[],
  target: MultiAirportOperationalDataTarget,
  errors: MultiAirportOperationalDataFailure[]
): T[] => {
  if (result.status === "fulfilled") {
    return Array.isArray(result.value) ? result.value : [];
  }

  airportCodes.forEach((airportCode) =>
    errors.push(createFailure(airportCode, target, "request", result.reason))
  );
  return [];
};

const addResponseFailure = (
  errors: MultiAirportOperationalDataFailure[],
  airportCode: string,
  target: MultiAirportOperationalDataTarget,
  response: { error?: unknown } | undefined
) => {
  if (!response) {
    errors.push(
      createFailure(
        airportCode,
        target,
        "response",
        `No ${target} response was returned for ${airportCode}`
      )
    );
  } else if (response.error) {
    errors.push(createFailure(airportCode, target, "response", response.error));
  }
};

export async function loadMultiAirportOperationalData(
  airportCodes: string[],
  dateRangePayloads: MultiAirportDateRangePayloads,
  accessToken: string,
  signal?: AbortSignal
): Promise<MultiAirportOperationalDataResult> {
  const uniqueAirportCodes = getUniqueAirportCodes(airportCodes);
  const errors: MultiAirportOperationalDataFailure[] = [];

  const otpPayload: MultiAirportOtpRequest[] = dateRangePayloads.otp.map(
    (range) => ({ ...range, tz: "LT" })
  );
  const flightsPayload: MultiAirportFlightsRequest[] =
    dateRangePayloads.flights.map(
      ({ airportCode, start, end }): MultiAirportFlightsRequest => ({
        airportCode,
        start,
        end,
        tz: "LT",
        timeMode: "Best",
      })
    );
  const notificationPayload: MultiAirportNotificationCountRequest = {
    airportCodes: uniqueAirportCodes,
  };

  const otpRequestCodes = getUniqueAirportCodes(
    otpPayload.map(({ airportCode }) => airportCode)
  );
  const flightsRequestCodes = getUniqueAirportCodes(
    flightsPayload.map(({ airportCode }) => airportCode)
  );

  const requests = [
    otpPayload.length > 0
      ? postJson<MultiAirportKpiResponse[]>(
          MULTI_AIRPORT_OTP_URL,
          otpPayload,
          accessToken,
          signal
        )
      : Promise.resolve<MultiAirportKpiResponse[]>([]),
    flightsPayload.length > 0
      ? postJson<MultiAirportKpiResponse[]>(
          MULTI_AIRPORT_FLIGHTS_URL,
          flightsPayload,
          accessToken,
          signal
        )
      : Promise.resolve<MultiAirportKpiResponse[]>([]),
    uniqueAirportCodes.length > 0
      ? postJson<MultiAirportNotificationCountResponse[]>(
          MULTI_AIRPORT_NOTIFICATIONS_URL,
          notificationPayload,
          accessToken,
          signal
        )
      : Promise.resolve<MultiAirportNotificationCountResponse[]>([]),
  ] as const;

  const [otpResult, flightsResult, notificationsResult] =
    await Promise.allSettled(requests);

  const otpResponses = getSettledResponses(
    otpResult,
    otpRequestCodes,
    "otp",
    errors
  );
  const flightsResponses = getSettledResponses(
    flightsResult,
    flightsRequestCodes,
    "flights",
    errors
  );
  const notificationResponses = getSettledResponses(
    notificationsResult,
    uniqueAirportCodes,
    "notifications",
    errors
  );

  const otpByAirport = getResponseByAirport(otpResponses);
  const flightsByAirport = getResponseByAirport(flightsResponses);
  const notificationsByAirport = getResponseByAirport(notificationResponses);
  const otpRequestCodeSet = new Set(otpRequestCodes);
  const flightsRequestCodeSet = new Set(flightsRequestCodes);

  const dataByAirport = uniqueAirportCodes.reduce<
    Record<string, AirportOperationalData>
  >((data, airportCode) => {
    const otpResponse = otpByAirport.get(airportCode);
    const flightsResponse = flightsByAirport.get(airportCode);
    const notificationResponse = notificationsByAirport.get(airportCode);

    if (!otpRequestCodeSet.has(airportCode)) {
      errors.push(
        createFailure(
          airportCode,
          "otp",
          "payload",
          `No OTP date range is available for ${airportCode}`
        )
      );
    } else if (otpResult.status === "fulfilled") {
      addResponseFailure(errors, airportCode, "otp", otpResponse);
    }

    if (!flightsRequestCodeSet.has(airportCode)) {
      errors.push(
        createFailure(
          airportCode,
          "flights",
          "payload",
          `No flights date range is available for ${airportCode}`
        )
      );
    } else if (flightsResult.status === "fulfilled") {
      addResponseFailure(errors, airportCode, "flights", flightsResponse);
    }

    if (notificationsResult.status === "fulfilled") {
      addResponseFailure(
        errors,
        airportCode,
        "notifications",
        notificationResponse
      );
    }

    data[airportCode] = {
      otp: adaptMultiAirportOtp(otpResponse),
      flights: adaptMultiAirportFlights(flightsResponse),
      averageDelay: clone(EMPTY_AIRPORT_OPERATIONAL_DATA.averageDelay),
      notifications: adaptMultiAirportNotifications(notificationResponse),
    };
    return data;
  }, {});

  return { dataByAirport, errors };
}
